/**
 * Problem Declaration Form (DC-722, DC-923)
 *
 * Form for declaring the ML problem being solved with this data.
 * Captures problem type, target column, domain, sensitivity level,
 * protected attributes for fairness analysis, and FDA model risk assessment.
 *
 * Following BusinessConfigForm.tsx pattern.
 */

import React, { useState, useEffect } from 'react';
import './contextual-validity.css';
import { ModelRiskMatrix } from './ModelRiskMatrix';
import type { ModelInfluence, DecisionConsequence } from './ModelRiskMatrix';

// ============================================================================
// TYPES
// ============================================================================

type RegulatoryFramework = 'fda_ai_ml' | 'fda_21cfr11' | 'fda_gxp' | 'ich_e6' | 'ich_e9' | 'hipaa' | 'gdpr' | 'eu_ai_act' | 'iso_13485' | 'other';

type BusinessPerspective = 'rnd' | 'quality' | 'commercial';

type GxpClassification = 'GMP' | 'GLP' | 'GCP' | 'GDP' | 'GVP';
type DataCriticality = 'critical_cpp_cqa' | 'major_in_process' | 'minor_supporting';
type QualityEventContext = 'routine_operations' | 'active_deviation' | 'capa_effectiveness_review'
  | 'annual_product_review' | 'regulatory_submission' | 'pai_prep' | 'post_market_surveillance';
type DataGenerationSource = 'validated_lims' | 'validated_erp' | 'mes_scada' | 'validated_edms'
  | 'spreadsheet' | 'manual_paper_digitized' | 'unvalidated_database';

interface ProblemDeclaration {
  journey_id: string;
  problem_statement: string;
  problem_type: string;
  target_column?: string;
  domain: string;
  sensitivity_level: string;
  protected_attributes: string[];
  intended_use?: string;
  regulatory_context?: string;
  confirmed_by_user: boolean;
  // FDA Model Risk fields (DC-923)
  model_influence?: ModelInfluence;
  decision_consequence?: DecisionConsequence;
  regulatory_frameworks?: RegulatoryFramework[];
  // Business Perspective fields
  business_perspective?: BusinessPerspective;
  business_objective?: string;
  business_objective_type?: string;
  kpis?: string[];
  source_systems?: string[];
  quality_standards?: string[];
  // Quality-specific fields
  gxp_classification?: GxpClassification;
  data_criticality?: DataCriticality;
  quality_event_context?: QualityEventContext;
  data_generation_source?: DataGenerationSource;
}

interface ProblemDeclarationFormProps {
  journeyId: string;
  onSave: (declaration: ProblemDeclaration) => void;
  onCancel?: () => void;
  existingDeclaration?: ProblemDeclaration | null;
  mode?: 'inline' | 'modal';
  perspective?: BusinessPerspective;
}

interface DeclarationOptions {
  problem_types: Array<{ value: string; label: string; description: string }>;
  domains: Array<{ value: string; label: string }>;
  sensitivity_levels: Array<{ value: string; label: string; description: string }>;
  common_protected_attributes: string[];
  // FDA AI/ML Guidance options (DC-923)
  model_influence_options?: Array<{ value: string; label: string; description: string }>;
  decision_consequence_options?: Array<{ value: string; label: string; description: string }>;
  regulatory_frameworks?: Array<{ value: string; label: string }>;
}

// ============================================================================
// DEFAULT OPTIONS (fallback if API fails)
// ============================================================================

const DEFAULT_OPTIONS: DeclarationOptions = {
  problem_types: [
    { value: 'binary_classification', label: 'Binary Classification', description: 'Two-class prediction (yes/no, pass/fail)' },
    { value: 'multiclass_classification', label: 'Multi-class Classification', description: 'Multiple category prediction' },
    { value: 'regression', label: 'Regression', description: 'Continuous value prediction' },
    { value: 'clustering', label: 'Clustering', description: 'Grouping similar items' },
    { value: 'time_series', label: 'Time Series', description: 'Temporal pattern prediction' },
    { value: 'ranking', label: 'Ranking', description: 'Ordering items by relevance' },
    { value: 'recommendation', label: 'Recommendation', description: 'Suggesting items to users' },
    { value: 'anomaly_detection', label: 'Anomaly Detection', description: 'Finding outliers or unusual patterns' },
    { value: 'nlp', label: 'NLP / Text Processing', description: 'Natural language understanding tasks' },
    { value: 'other', label: 'Other', description: 'Custom or specialized problem type' },
  ],
  domains: [
    { value: 'healthcare', label: 'Healthcare / Life Sciences' },
    { value: 'pharma', label: 'Pharmaceuticals' },
    { value: 'biotech', label: 'Biotechnology' },
    { value: 'finance', label: 'Finance / Banking' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'retail', label: 'Retail / E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'government', label: 'Government / Public Sector' },
    { value: 'education', label: 'Education' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'legal', label: 'Legal' },
    { value: 'other', label: 'Other' },
  ],
  sensitivity_levels: [
    { value: 'low', label: 'Low', description: 'Minimal risk - internal analytics, research' },
    { value: 'medium', label: 'Medium', description: 'Moderate risk - business decisions, recommendations' },
    { value: 'high', label: 'High', description: 'High risk - impacts individuals, requires fairness review' },
    { value: 'critical', label: 'Critical', description: 'Critical - regulatory, healthcare, legal decisions' },
  ],
  common_protected_attributes: [
    'age', 'gender', 'race', 'ethnicity', 'religion', 'nationality',
    'disability', 'marital_status', 'sexual_orientation', 'veteran_status',
    'pregnancy', 'genetic_information', 'zip_code', 'income_level',
  ],
  // FDA AI/ML Guidance options (DC-923)
  model_influence_options: [
    { value: 'low', label: 'Low', description: 'Model provides suggestions only; humans make all decisions' },
    { value: 'medium', label: 'Medium', description: 'Model recommendations influence but don\'t determine decisions' },
    { value: 'high', label: 'High', description: 'Model output directly drives decisions with limited human review' },
  ],
  decision_consequence_options: [
    { value: 'low', label: 'Low', description: 'Wrong output has minimal impact; easily reversible' },
    { value: 'medium', label: 'Medium', description: 'Wrong output causes inconvenience or moderate harm' },
    { value: 'high', label: 'High', description: 'Wrong output causes significant harm or financial loss' },
    { value: 'critical', label: 'Critical', description: 'Wrong output may cause irreversible harm, injury, or death' },
  ],
  regulatory_frameworks: [
    { value: 'fda_ai_ml', label: 'FDA AI/ML Guidance' },
    { value: 'fda_21cfr11', label: 'FDA 21 CFR Part 11' },
    { value: 'fda_gxp', label: 'FDA GxP (GLP/GCP/GMP)' },
    { value: 'ich_e6', label: 'ICH E6 (GCP)' },
    { value: 'ich_e9', label: 'ICH E9 (Statistics)' },
    { value: 'hipaa', label: 'HIPAA' },
    { value: 'gdpr', label: 'GDPR' },
    { value: 'eu_ai_act', label: 'EU AI Act' },
    { value: 'iso_13485', label: 'ISO 13485 (Medical Devices)' },
    { value: 'other', label: 'Other' },
  ],
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProblemDeclarationForm: React.FC<ProblemDeclarationFormProps> = ({
  journeyId,
  onSave,
  onCancel,
  existingDeclaration,
  mode = 'inline',
  perspective = 'rnd',
}) => {
  const isCommercial = perspective === 'commercial';
  const isQuality = perspective === 'quality';
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [options, setOptions] = useState<DeclarationOptions>(DEFAULT_OPTIONS);

  const getDefaultFormValues = (): ProblemDeclaration => {
    const base = {
      journey_id: journeyId,
      problem_statement: '',
      problem_type: perspective === 'commercial' ? 'attribution_modeling' :
                     perspective === 'quality' ? 'process_validation' : 'binary_classification',
      target_column: '',
      domain: perspective === 'commercial' ? 'pharma' :
              perspective === 'quality' ? 'manufacturing' : 'healthcare',
      sensitivity_level: perspective === 'quality' ? 'high' : 'medium',
      protected_attributes: [],
      intended_use: '',
      regulatory_context: '',
      confirmed_by_user: false,
      model_influence: undefined as ModelInfluence | undefined,
      decision_consequence: undefined as DecisionConsequence | undefined,
      regulatory_frameworks: [] as RegulatoryFramework[],
      business_perspective: perspective,
      kpis: [] as string[],
      source_systems: [] as string[],
      quality_standards: [] as string[],
      gxp_classification: undefined as GxpClassification | undefined,
      data_criticality: undefined as DataCriticality | undefined,
      quality_event_context: undefined as QualityEventContext | undefined,
      data_generation_source: undefined as DataGenerationSource | undefined,
    };
    return base;
  };

  const [formValues, setFormValues] = useState<ProblemDeclaration>(getDefaultFormValues());

  const [customAttribute, setCustomAttribute] = useState('');

  // ============================================================================
  // LIFECYCLE: Load options and existing declaration
  // ============================================================================

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch options with perspective
      const optionsResponse = await fetch(`/api/contextual-validity/declarations/options?perspective=${perspective}`);
      if (optionsResponse.ok) {
        const optionsData = await optionsResponse.json();
        setOptions(optionsData);
      }

      // If editing, load existing declaration
      if (existingDeclaration) {
        setFormValues(existingDeclaration);
      } else {
        // Try to fetch existing declaration for this journey
        const declResponse = await fetch(`/api/contextual-validity/declarations/${journeyId}`);
        if (declResponse.ok) {
          const declData = await declResponse.json();
          if (declData.declaration) {
            setFormValues(declData.declaration);
          }
        }
      }
    } catch (err: unknown) {
      console.error('[ProblemDeclarationForm] Load error:', err);
      // Don't show error - just use defaults
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleInputChange = (field: keyof ProblemDeclaration, value: string | string[] | boolean) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setValidationErrors([]); // Clear errors on change
  };

  const handleProtectedAttributeToggle = (attr: string) => {
    setFormValues(prev => {
      const current = prev.protected_attributes || [];
      if (current.includes(attr)) {
        return { ...prev, protected_attributes: current.filter(a => a !== attr) };
      } else {
        return { ...prev, protected_attributes: [...current, attr] };
      }
    });
  };

  const handleAddCustomAttribute = () => {
    const trimmed = customAttribute.trim().toLowerCase().replace(/\s+/g, '_');
    if (trimmed && !formValues.protected_attributes.includes(trimmed)) {
      setFormValues(prev => ({
        ...prev,
        protected_attributes: [...prev.protected_attributes, trimmed],
      }));
      setCustomAttribute('');
    }
  };

  // FDA Model Risk handlers (DC-923)
  const handleRegulatoryFrameworkToggle = (framework: RegulatoryFramework) => {
    setFormValues(prev => {
      const current = prev.regulatory_frameworks || [];
      if (current.includes(framework)) {
        return { ...prev, regulatory_frameworks: current.filter(f => f !== framework) };
      } else {
        return { ...prev, regulatory_frameworks: [...current, framework] };
      }
    });
  };

  const handleValidate = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/contextual-validity/declarations/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });

      const data = await response.json();

      // API returns { success, validation: { valid, errors, warnings, suggestions } }
      const validation = data.validation || data;
      if (!validation.valid) {
        setValidationErrors(validation.errors || ['Validation failed']);
        return false;
      }

      return true;
    } catch (err) {
      console.error('[ProblemDeclarationForm] Validation error:', err);
      // Allow submit anyway if validation endpoint fails
      return true;
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate first
      const isValid = await handleValidate();
      if (!isValid) {
        setSaving(false);
        return;
      }

      // Save declaration
      const response = await fetch('/api/contextual-validity/declarations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formValues,
          confirmed_by_user: true,
          business_perspective: perspective,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save declaration');
      }

      const data = await response.json();
      onSave(data.declaration);
    } catch (err: unknown) {
      console.error('[ProblemDeclarationForm] Submit error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // RENDER: Loading state
  // ============================================================================

  if (loading) {
    return (
      <div className="problem-declaration-form loading">
        <div className="spinner-small"></div>
        <span>Loading form...</span>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Main form
  // ============================================================================

  const selectedProblemType = options.problem_types.find(t => t.value === formValues.problem_type);
  const selectedSensitivity = options.sensitivity_levels.find(s => s.value === formValues.sensitivity_level);

  return (
    <div className={`problem-declaration-form ${mode}`}>
      <div className="form-header">
        <h3>{isCommercial ? 'Define Commercial Objective' : isQuality ? 'Define Quality Objective' : 'Declare ML Problem'}</h3>
        <p className="form-subtitle">
          {isCommercial
            ? 'Define your commercial analytics objective to enable source system consistency and attribution analysis.'
            : isQuality
            ? 'Define your quality objective to enable ALCOA+ data integrity and process compliance assessment.'
            : 'Define the machine learning problem you\'re solving to enable contextual validation and fairness analysis.'}
        </p>
      </div>

      {error && (
        <div className="form-error">
          <span className="error-icon">&#9888;</span>
          {error}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="form-validation-errors">
          <strong>Please fix the following:</strong>
          <ul>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-body">
        {/* Problem Statement / Business Objective */}
        <div className="form-group">
          <label htmlFor="problem_statement">
            {isCommercial ? 'Business Objective *' : isQuality ? 'Quality Objective *' : 'Problem Statement *'}
          </label>
          <textarea
            id="problem_statement"
            value={formValues.problem_statement}
            onChange={(e) => handleInputChange('problem_statement', e.target.value)}
            placeholder={
              isCommercial
                ? "Describe the commercial analytics objective. E.g., 'Multi-touch attribution for ONCORIX marketing campaigns across digital and field channels'"
                : isQuality
                ? "Describe the quality assessment objective. E.g., 'Assess data integrity of batch release records for Product X manufacturing line'"
                : "Describe the ML problem you're trying to solve. E.g., 'Predict patient readmission risk within 30 days of discharge'"
            }
            rows={3}
            required
          />
          <span className="field-hint">
            {isCommercial ? 'Be specific about the business question and data sources involved' :
             isQuality ? 'Be specific about the quality decision this data supports' :
             'Be specific about what you\'re predicting or classifying'}
          </span>
        </div>

        {/* Problem Type */}
        <div className="form-group">
          <label htmlFor="problem_type">
            {isCommercial ? 'Analysis Type *' : isQuality ? 'Assessment Type *' : 'Problem Type *'}
          </label>
          <select
            id="problem_type"
            value={formValues.problem_type}
            onChange={(e) => handleInputChange('problem_type', e.target.value)}
          >
            {options.problem_types.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {selectedProblemType && (
            <span className="field-hint">{selectedProblemType.description}</span>
          )}
        </div>

        {/* Target Column (R&D only) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label htmlFor="target_column">Target Column</label>
            <input
              type="text"
              id="target_column"
              value={formValues.target_column || ''}
              onChange={(e) => handleInputChange('target_column', e.target.value)}
              placeholder="E.g., 'readmitted', 'outcome', 'label'"
            />
            <span className="field-hint">The column name containing the value you want to predict (if known)</span>
          </div>
        )}

        {/* Domain (R&D only - auto-set for Commercial/Quality) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label htmlFor="domain">Domain *</label>
            <select
              id="domain"
              value={formValues.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
            >
              {options.domains.map(domain => (
                <option key={domain.value} value={domain.value}>
                  {domain.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Sensitivity Level (R&D only - auto-set for Commercial/Quality) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label>Sensitivity Level *</label>
            <div className="sensitivity-selector">
              {options.sensitivity_levels.map(level => (
                <button
                  key={level.value}
                  type="button"
                  className={`sensitivity-option ${formValues.sensitivity_level === level.value ? 'selected' : ''} sensitivity-${level.value}`}
                  onClick={() => handleInputChange('sensitivity_level', level.value)}
                >
                  <span className="sensitivity-label">{level.label}</span>
                </button>
              ))}
            </div>
            {selectedSensitivity && (
              <span className="field-hint">{selectedSensitivity.description}</span>
            )}
          </div>
        )}

        {/* FDA Model Risk Assessment (R&D only) */}
        {!isCommercial && !isQuality && (
          <ModelRiskMatrix
            modelInfluence={formValues.model_influence}
            decisionConsequence={formValues.decision_consequence}
            onInfluenceChange={(value) => handleInputChange('model_influence', value)}
            onConsequenceChange={(value) => handleInputChange('decision_consequence', value)}
            disabled={saving}
          />
        )}

        {/* Regulatory Frameworks (R&D only — Quality uses new dedicated fields) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label>Regulatory Frameworks</label>
            <p className="field-explanation">
              Select applicable regulatory frameworks for this AI/ML application.
            </p>
            <div className="regulatory-frameworks-grid">
              {(options.regulatory_frameworks || DEFAULT_OPTIONS.regulatory_frameworks || []).map(framework => (
                <label key={framework.value} className="framework-checkbox">
                  <input
                    type="checkbox"
                    checked={(formValues.regulatory_frameworks || []).includes(framework.value as RegulatoryFramework)}
                    onChange={() => handleRegulatoryFrameworkToggle(framework.value as RegulatoryFramework)}
                    disabled={saving}
                  />
                  <span>{framework.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Protected Attributes (R&D only) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label>Protected Attributes</label>
            <p className="field-explanation">
              Select attributes that should not influence model predictions unfairly.
              These will be checked for proxy correlations.
            </p>
            <div className="protected-attributes-grid">
              {options.common_protected_attributes.map(attr => (
                <label key={attr} className="attribute-checkbox">
                  <input
                    type="checkbox"
                    checked={formValues.protected_attributes.includes(attr)}
                    onChange={() => handleProtectedAttributeToggle(attr)}
                  />
                  <span>{attr.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>

            {/* Custom attributes */}
            {formValues.protected_attributes.filter(a => !options.common_protected_attributes.includes(a)).length > 0 && (
              <div className="custom-attributes">
                <span className="custom-label">Custom:</span>
                {formValues.protected_attributes
                  .filter(a => !options.common_protected_attributes.includes(a))
                  .map(attr => (
                    <span key={attr} className="custom-attribute-tag">
                      {attr}
                      <button
                        type="button"
                        onClick={() => handleProtectedAttributeToggle(attr)}
                        className="remove-tag"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
              </div>
            )}

            <div className="add-custom-attribute">
              <input
                type="text"
                value={customAttribute}
                onChange={(e) => setCustomAttribute(e.target.value)}
                placeholder="Add custom attribute..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomAttribute())}
              />
              <button type="button" onClick={handleAddCustomAttribute} className="btn-small">
                Add
              </button>
            </div>
          </div>
        )}

        {/* Source Systems (Commercial only) */}
        {isCommercial && (
          <div className="form-group">
            <label>Source Systems</label>
            <p className="field-explanation">
              Select the source systems this data was merged from.
            </p>
            <div className="source-systems-grid">
              {['Veeva CRM', 'Salesforce', 'IQVIA', 'SAP', 'Marketo',
                'Google Ads', 'Meta Ads', 'LinkedIn Ads', 'SFMC', 'Adobe Analytics'].map(system => (
                <label key={system} className="attribute-checkbox">
                  <input
                    type="checkbox"
                    checked={(formValues.source_systems || []).includes(system)}
                    onChange={() => {
                      const current = formValues.source_systems || [];
                      const updated = current.includes(system)
                        ? current.filter(s => s !== system)
                        : [...current, system];
                      handleInputChange('source_systems', updated);
                    }}
                  />
                  <span>{system}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* KPIs (Commercial only) */}
        {isCommercial && (
          <div className="form-group">
            <label>Key Performance Indicators (KPIs)</label>
            <p className="field-explanation">
              Select the KPIs this analysis aims to measure or optimize.
            </p>
            <div className="kpi-grid">
              {['TRx Lift', 'NRx Lift', 'ROI', 'Market Share', 'Share of Voice',
                'HCP Reach', 'Engagement Score', 'Cost Per Acquisition', 'CLV'].map(kpi => (
                <label key={kpi} className="attribute-checkbox">
                  <input
                    type="checkbox"
                    checked={(formValues.kpis || []).includes(kpi)}
                    onChange={() => {
                      const current = formValues.kpis || [];
                      const updated = current.includes(kpi)
                        ? current.filter(k => k !== kpi)
                        : [...current, kpi];
                      handleInputChange('kpis', updated);
                    }}
                  />
                  <span>{kpi}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* GxP Classification (Quality only) */}
        {isQuality && (
          <div className="form-group">
            <label>GxP Classification</label>
            <p className="field-explanation">
              Which Good Practice area governs this data?
            </p>
            <div className="chip-selector-grid">
              {([
                { value: 'GMP', label: 'GMP', desc: 'Good Manufacturing Practice' },
                { value: 'GLP', label: 'GLP', desc: 'Good Laboratory Practice' },
                { value: 'GCP', label: 'GCP', desc: 'Good Clinical Practice' },
                { value: 'GDP', label: 'GDP', desc: 'Good Distribution Practice' },
                { value: 'GVP', label: 'GVP', desc: 'Good Pharmacovigilance Practice' },
              ] as const).map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`chip-option ${formValues.gxp_classification === item.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('gxp_classification',
                    formValues.gxp_classification === item.value ? '' : item.value)}
                  title={item.desc}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Data Criticality (Quality only) */}
        {isQuality && (
          <div className="form-group">
            <label>Data Criticality</label>
            <p className="field-explanation">
              How critical is this data to product quality decisions?
            </p>
            <div className="chip-selector-grid">
              {([
                { value: 'critical_cpp_cqa', label: 'Critical (CPP/CQA)', desc: 'Critical Process Parameters / Critical Quality Attributes' },
                { value: 'major_in_process', label: 'Major (In-Process Controls)', desc: 'In-process monitoring and controls' },
                { value: 'minor_supporting', label: 'Minor (Supporting)', desc: 'Supporting documentation and records' },
              ] as const).map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`chip-option ${formValues.data_criticality === item.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('data_criticality',
                    formValues.data_criticality === item.value ? '' : item.value)}
                  title={item.desc}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quality Event Context (Quality only) */}
        {isQuality && (
          <div className="form-group">
            <label>Quality Event Context</label>
            <p className="field-explanation">
              What quality activity is this data being assessed for?
            </p>
            <div className="chip-selector-grid">
              {([
                { value: 'routine_operations', label: 'Routine Operations' },
                { value: 'active_deviation', label: 'Active Deviation Investigation' },
                { value: 'capa_effectiveness_review', label: 'CAPA Effectiveness Review' },
                { value: 'annual_product_review', label: 'Annual Product Review (APR)' },
                { value: 'regulatory_submission', label: 'Regulatory Submission' },
                { value: 'pai_prep', label: 'Pre-Approval Inspection (PAI) Prep' },
                { value: 'post_market_surveillance', label: 'Post-Market Surveillance' },
              ] as const).map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`chip-option ${formValues.quality_event_context === item.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('quality_event_context',
                    formValues.quality_event_context === item.value ? '' : item.value)}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Data Generation Source (Quality only) */}
        {isQuality && (
          <div className="form-group">
            <label>Data Generation Source</label>
            <p className="field-explanation">
              What system generated this data? This affects trust level and audit trail expectations.
            </p>
            <div className="chip-selector-grid">
              {([
                { value: 'validated_lims', label: 'Validated LIMS' },
                { value: 'validated_erp', label: 'Validated ERP (SAP QM/similar)' },
                { value: 'mes_scada', label: 'MES/SCADA' },
                { value: 'validated_edms', label: 'Validated eCTD/eDMS' },
                { value: 'spreadsheet', label: 'Spreadsheet (Excel/Sheets)' },
                { value: 'manual_paper_digitized', label: 'Manual/Paper Records (Digitized)' },
                { value: 'unvalidated_database', label: 'Unvalidated Database' },
              ] as const).map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`chip-option ${formValues.data_generation_source === item.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('data_generation_source',
                    formValues.data_generation_source === item.value ? '' : item.value)}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Intended Use */}
        <div className="form-group">
          <label htmlFor="intended_use">Intended Use</label>
          <textarea
            id="intended_use"
            value={formValues.intended_use || ''}
            onChange={(e) => handleInputChange('intended_use', e.target.value)}
            placeholder={
              isCommercial
                ? "How will this analysis be used? E.g., 'Optimize Q3 promotional spend across digital and field channels'"
                : isQuality
                ? "What quality decision does this data support? E.g., 'Batch release decision for Product X Lot 2024-0892'"
                : "How will this model be used? E.g., 'Clinical decision support for nurse care managers'"
            }
            rows={2}
          />
        </div>

        {/* Regulatory Context (R&D only - Quality has its own standards section) */}
        {!isCommercial && !isQuality && (
          <div className="form-group">
            <label htmlFor="regulatory_context">Regulatory Context</label>
            <input
              type="text"
              id="regulatory_context"
              value={formValues.regulatory_context || ''}
              onChange={(e) => handleInputChange('regulatory_context', e.target.value)}
              placeholder="E.g., 'HIPAA', 'GDPR', 'FDA 21 CFR Part 11'"
            />
            <span className="field-hint">Any regulatory frameworks this model must comply with</span>
          </div>
        )}
      </div>

      <div className="form-actions">
        {onCancel && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn-primary"
          onClick={handleSubmit}
          disabled={saving || !formValues.problem_statement.trim()}
        >
          {saving ? (
            <>
              <span className="spinner-small"></span>
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default ProblemDeclarationForm;
